import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import apiClient from "@/utils/apiClient";
import { Users, Calendar, Phone, Heart, Activity, AlertCircle, CheckCircle, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const WardenHome = ({ permissions }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingTasks, setPendingTasks] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchPendingTasks();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/v1/warden/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats", error);
      // Set default values on error
      setStats({
        total_students: 0,
        present_today: 0,
        absent_today: 0,
        on_leave: 0,
        health_issues: 0,
        recent_activities: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingTasks = async () => {
    try {
      const response = await apiClient.get('/v1/warden/dashboard/pending-tasks');
      setPendingTasks(response.data);
    } catch (error) {
      console.error("Failed to fetch pending tasks:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="warden-home" className="space-y-6">
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold mb-2" style={{ color: "#2C5F7F" }}>
          Welcome to Your Dashboard
        </h1>
        <p className="text-gray-600">Here&apos;s what&apos;s happening with your assigned students today</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="glass-effect card-hover" data-testid="total-students-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">My Students</CardTitle>
            <Users className="h-5 w-5" style={{ color: "#7CB9E8" }} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: "#2C5F7F" }}>
              {stats?.total_students || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">Total assigned to you</p>
          </CardContent>
        </Card>

        <Card className="glass-effect card-hover" data-testid="attendance-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Present Today</CardTitle>
            <Calendar className="h-5 w-5" style={{ color: "#7CB9E8" }} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: "#2C5F7F" }}>
              {stats?.present_today || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">Out of {stats?.total_students || 0} students</p>
          </CardContent>
        </Card>

        <Card className="glass-effect card-hover" data-testid="health-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Health Issues (7 days)</CardTitle>
            <Heart className="h-5 w-5" style={{ color: "#7CB9E8" }} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: "#2C5F7F" }}>
              {stats?.health_issues || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">Last 7 days</p>
          </CardContent>
        </Card>

        <Card className="glass-effect card-hover" data-testid="activities-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Recent Activities</CardTitle>
            <Activity className="h-5 w-5" style={{ color: "#7CB9E8" }} />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ color: "#2C5F7F" }}>
              {stats?.recent_activities || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">Last 30 days</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Tasks */}
      {pendingTasks && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              Pending Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {pendingTasks.attendance_pending > 0 && permissions?.can_update_attendance && (
                <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="font-medium text-orange-900">Attendance Needed</p>
                      <p className="text-sm text-orange-700">
                        {pendingTasks.attendance_pending} students missing attendance (last 7 days)
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => navigate('/warden/attendance')}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Update
                  </Button>
                </div>
              )}

              {pendingTasks.health_pending > 0 && permissions?.can_update_health && (
                <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Heart className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="font-medium text-red-900">Health Checkup Needed</p>
                      <p className="text-sm text-red-700">
                        {pendingTasks.health_pending} students need health checkup (last 7 days)
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => navigate('/warden/health')}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Add
                  </Button>
                </div>
              )}

              {pendingTasks.activities_pending > 0 && permissions?.can_update_activities && (
                <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Activity className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium text-blue-900">Activities Needed</p>
                      <p className="text-sm text-blue-700">
                        {pendingTasks.activities_pending} students have no activities logged (last 7 days)
                      </p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => navigate('/warden/activities')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Add
                  </Button>
                </div>
              )}

              {pendingTasks.attendance_pending === 0 && pendingTasks.health_pending === 0 && pendingTasks.activities_pending === 0 && (
                <div className="text-center py-4 text-gray-600">
                  <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
                  <p>All weekly tasks completed! Great job! 🎉</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="mt-8 glass-effect p-6 rounded-xl">
        <h2 className="text-xl font-semibold mb-4" style={{ color: "#2C5F7F" }}>
          Quick Actions
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {permissions?.can_update_attendance && (
            <Button 
              variant="outline" 
              className="h-20 flex flex-col gap-2"
              onClick={() => navigate('/warden/attendance')}
            >
              <Calendar className="w-5 h-5" />
              <span className="text-sm">Mark Attendance</span>
            </Button>
          )}
          {permissions?.can_update_health && (
            <Button 
              variant="outline" 
              className="h-20 flex flex-col gap-2"
              onClick={() => navigate('/warden/health')}
            >
              <Heart className="w-5 h-5" />
              <span className="text-sm">Add Health Record</span>
            </Button>
          )}
          {permissions?.can_update_activities && (
            <Button 
              variant="outline" 
              className="h-20 flex flex-col gap-2"
              onClick={() => navigate('/warden/activities')}
            >
              <Activity className="w-5 h-5" />
              <span className="text-sm">Add Activity</span>
            </Button>
          )}
          <Button 
            variant="outline" 
            className="h-20 flex flex-col gap-2"
            onClick={() => navigate('/warden/students')}
          >
            <Users className="w-5 h-5" />
            <span className="text-sm">View Students</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WardenHome;